import { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HomeCurrencyDividendCard — Round 95h (Feb 2026).
 *
 * Companion to <StreakRiskChart /> for foreign OTC / ADR tickers
 * (F/Y suffix — Nestlé NSRGF, Roche RHHVF, Novartis NVSEF, SAP SAPIF,
 * etc.). Massive stores their dividends in the parent company's home
 * currency (CHF / EUR / DKK / CAD), so grouping by year in that
 * native currency reveals the real corporate dividend policy — while
 * our default USD sums are polluted by FX conversion swings.
 *
 * Reads `/api/stocks/{ticker}/home-currency-dividends`.
 *
 * Null-renders when the endpoint returns `is_foreign_adr: false`
 * (i.e. this ticker's dividends are already in USD — nothing to show).
 *
 * Props:
 *   ticker  — string, required
 *   compact — boolean, default false. Renders inline in a tighter
 *             layout when used inside the TierCutsRow chip expansion.
 */
export default function HomeCurrencyDividendCard({ ticker, compact = false, assumeForeign = false }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    let cancelled = false;
    if (!ticker) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/stocks/${encodeURIComponent(ticker)}/home-currency-dividends`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ is_foreign_adr: false });
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  if (!data) return null;

  // Sep 2026 — no home-currency ledger (Massive books many OTC ADRs in USD
  // only). When the caller flagged the ticker as foreign, show the USD-only
  // series with the dip year called out instead of rendering nothing.
  if (!data.is_foreign_adr) {
    if (!assumeForeign) return null;
    const usd = (data.usd_annual || []).slice(-7);
    const dip = data.usd_dip;
    return (
      <div className="mt-2 rounded-lg border border-amber-400/60 bg-amber-50/60 dark:bg-amber-950/30 p-3 text-xs" data-testid={`home-currency-card-usd-only-${ticker}`}>
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="text-[10px] font-black uppercase tracking-wider text-amber-800 dark:text-amber-200">FX check · {ticker}</span>
          <span className="px-1.5 py-0.5 rounded border text-[10px] font-bold bg-muted text-muted-foreground border-border">No home-currency record available</span>
        </div>
        {usd.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`home-currency-usd-series-${ticker}`}>
            {usd.map((y) => {
              const isDip = dip && y.year === dip.to_year;
              return (
                <span key={y.year} className={`font-mono px-1.5 py-0.5 rounded border ${isDip ? 'border-rose-400/70 bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 font-bold' : 'border-border bg-background/60'}`}>
                  {y.year} ${y.amount.toFixed(2)}{isDip ? ` (${dip.pct}%)` : ''}
                </span>
              );
            })}
          </div>
        )}
        <p className="leading-snug text-muted-foreground">
          No native-currency dividend history is available for {ticker} yet, so the home-currency streak can&apos;t be checked here.
          {dip ? ` The USD payout slipped ${Math.abs(dip.pct)}% from ${dip.from_year} to ${dip.to_year}` : ' The USD series shows a year-over-year dip'}
          {dip && Math.abs(dip.pct) < 5 ? ' — a move that small on a foreign OTC/ADR is usually exchange-rate drift, not a policy cut.' : '.'}
          {' '}Confirm on the issuer&apos;s investor-relations page in its native currency before treating it as a real cut.
        </p>
      </div>
    );
  }

  const homeSeries = data.home_annual || [];
  const homeGrowing = !!data.home_is_growing;
  const homeStreak = data.home_streak_years || 0;
  const currency = data.home_currency;

  // Slice the trailing 6 completed years for the mini table.
  const last6 = homeSeries.slice(-7);

  const badgeCls = homeGrowing
    ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-400/60'
    : 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 border-rose-400/60';

  return (
    <div
      className={`${compact ? 'mt-1.5' : 'mt-3'} rounded-lg border border-amber-400/40 bg-gradient-to-br from-amber-50/60 via-amber-50/30 to-transparent dark:from-amber-950/25 dark:via-amber-950/15 dark:to-transparent p-2.5`}
      data-testid="home-currency-dividend-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full text-left"
        data-testid="home-currency-toggle"
      >
        <Globe2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
        <span className="text-[12px] font-black uppercase tracking-wider text-amber-900 dark:text-amber-100">
          Home-Currency Streak
        </span>
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-[1px] rounded-md border text-[10px] font-bold ${badgeCls}`}
          data-testid="home-currency-streak-badge"
        >
          {homeGrowing ? (
            <CheckCircle2 className="w-3 h-3" />
          ) : (
            <AlertTriangle className="w-3 h-3" />
          )}
          {homeStreak}y {currency} {homeGrowing ? 'intact' : 'broken'}
        </span>
        <span className="ml-auto text-muted-foreground shrink-0">
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {data.advisory && (
            <p className="text-[11px] font-semibold text-amber-900 dark:text-amber-100 leading-snug">
              {data.advisory}
            </p>
          )}
          {data.home_split_adjusted && (
            <p className="text-[10px] italic text-amber-800/90 dark:text-amber-200/90 leading-snug" data-testid="home-currency-split-note">
              Home-currency amounts adjusted for the {data.home_split_note || 'stock split'} — earlier years are shown on today&rsquo;s share basis so the split does not read as a cut.
            </p>
          )}

          {last6.length > 0 && (
            <div className="rounded-md bg-white/60 dark:bg-zinc-900/60 border border-amber-400/30 overflow-hidden">
              <table className="w-full text-[11px]" data-testid="home-currency-table">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-amber-100/40 dark:bg-amber-900/20">
                  <tr>
                    <th className="text-left px-2 py-1 font-semibold">Year</th>
                    <th className="text-right px-2 py-1 font-semibold">{currency} paid</th>
                    <th className="text-right px-2 py-1 font-semibold">YoY</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {last6.map((row, idx) => {
                    const prev = idx > 0 ? last6[idx - 1].amount : null;
                    const pct = prev ? ((row.amount - prev) / prev) * 100 : null;
                    const arrow = pct == null ? '' : pct > 0.5 ? '↑' : pct < -0.5 ? '↓' : '→';
                    const arrowCls = pct == null
                      ? 'text-muted-foreground'
                      : pct > 0.5
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : pct < -0.5
                          ? 'text-rose-700 dark:text-rose-300'
                          : 'text-muted-foreground';
                    return (
                      <tr key={row.year} className="border-t border-amber-400/20">
                        <td className="px-2 py-1 text-foreground">{row.year}</td>
                        <td className="px-2 py-1 text-right text-foreground font-semibold">
                          {row.amount.toFixed(row.amount < 1 ? 4 : 2)}
                        </td>
                        <td className={`px-2 py-1 text-right ${arrowCls}`}>
                          {pct == null ? '—' : `${arrow} ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground leading-snug">
            Dividend history in the company&apos;s native currency ({currency}). Our default streak check
            sums payouts in USD — for a foreign ADR that hides real growth when the dollar strengthens.
          </p>
        </div>
      )}
    </div>
  );
}
